/**
 * SubjectsPage — admin view at /subjects.
 *
 * Reframed from "subject CRUD + member assignment" to "subject =
 * higher-scope directory for promoted files." Each subject is rendered
 * as a folder card whose contents are the files at
 * subjects/<slug>/ in Nova OS document storage. Approved promotions
 * land files here via DocumentMover (cmd/promotion-svc); this page is
 * the read+manage view over the result.
 *
 * Member management UI is intentionally dropped. The
 * /api/admin/subjects/{id}/members endpoints + nova_subject_members
 * table are kept intact so the data isn't lost; if access-control on
 * subject knowledge returns as a feature, the UI re-adds in one file.
 *
 * Caveat (v1): Nova OS scopes /api/documents/* under the caller's
 * users/<id>/ home directory. That means each admin sees their own
 * subjects/<slug>/ directory rather than a school-shared one. On
 * bosong there's a single admin so this is invisible; in a multi-
 * admin school we'd need an admin-shared scope from Nova OS to make
 * the promoted library uniform across admins.
 */
import { createPortal } from "react-dom";
import { LEVEL_KEYS, LevelBadge } from "../../components/SubjectLevel";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../api/school";
import { FolderPanel } from "../workbench/FolderPanel";

interface Subject {
  levels?: string[];
  id: string;
  slug: string;
  name: string;
  description: string | null;
  member_count: number;
  role_type?: string;
  status?: string;
  module_visibility?: Record<string, string> | null;
  created_at: string;
}

async function fetchSubjects(): Promise<Subject[]> {
  const r = await fetch("/api/admin/subjects", { headers: authHeaders() });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${r.status}`);
  }
  return r.json();
}

async function createSubject(input: {
  slug: string;
  name: string;
  description?: string;
}) {
  const r = await fetch("/api/admin/subjects", {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${r.status}`);
  }
  return r.json();
}

export default function SubjectsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const subjects = useQuery({
    queryKey: ["admin-subjects"],
    queryFn: fetchSubjects,
    refetchInterval: 30_000,
    retry: false,
  });

  const createMut = useMutation({
    mutationFn: createSubject,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-subjects"] }),
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5 p-4 text-slate-800 md:p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("subjects.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            {t("subjects.subtitle")}
          </p>
        </div>
        <CreateSubjectButton
          onCreate={createMut.mutate}
          disabled={createMut.isPending}
        />
      </header>

      {createMut.error && (
        <p className="text-xs text-red-600">
          {t("subjects.errors.createFailed")}:{" "}
          {(createMut.error as Error).message}
        </p>
      )}

      {subjects.isLoading ? (
        <p className="text-slate-500 italic text-sm">{t("subjects.loading")}</p>
      ) : subjects.error ? (
        <p className="text-slate-500 text-sm italic">
          {t("subjects.loadError", {
            msg: (subjects.error as Error).message,
          })}
        </p>
      ) : (subjects.data ?? []).length === 0 ? (
        <p className="text-slate-500 text-sm italic">{t("subjects.empty")}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {subjects.data!.map((s) => (
            <SubjectCard key={s.id} subject={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectCard({ subject }: { subject: Subject }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const archived = subject.status === "archived";
  const delMut = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/admin/subjects/${subject.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) {
        const b = (await r.json().catch(() => ({}))) as { detail?: { error?: string; members?: number } };
        const code = b.detail?.error;
        throw new Error(
          code === "HAS_MEMBERS"
            ? t("subjects.deleteHasMembers", { count: b.detail?.members ?? 0 })
            : code === "ARCHIVE_FIRST"
              ? t("subjects.deleteArchiveFirst")
              : `HTTP ${r.status}`,
        );
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-subjects"] }),
    onError: (e) => window.alert((e as Error).message),
  });
  // 学科改名(博颂 2026-09-28):行政班下的课程名、学科助教一起跟着改
  const renameMut = useMutation({
    mutationFn: async () => {
      const name = window.prompt(t("subjects.renamePrompt"), subject.name)?.trim();
      if (!name || name === subject.name) return;
      const r = await fetch(`/api/admin/subjects/${subject.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { detail?: string }).detail ?? String(r.status));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-subjects"] });
      void queryClient.invalidateQueries({ queryKey: ["school"] });
    },
    onError: (e) => window.alert((e as Error).message),
  });
  const [showVis, setShowVis] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  const archiveMut = useMutation({
    mutationFn: async () => {
      const r = await fetch(
        `/api/admin/subjects/${subject.id}/${archived ? "unarchive" : "archive"}`,
        { method: "POST", headers: authHeaders() },
      );
      if (!r.ok) throw new Error(String(r.status));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-subjects"] }),
  });
  return (
    <div className={archived ? "opacity-50" : ""}>
      <div className="mb-1 flex items-center gap-2 text-xs">
        <span className={`px-1.5 py-0.5 rounded ${
          subject.role_type === "functional"
            ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"
        }`}>
          {subject.role_type === "functional" ? t("subjects.functional") : t("subjects.academic")}
        </span>
        {archived && (
          <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">{t("subjects.archived")}</span>
        )}
        <button
          onClick={() => renameMut.mutate()}
          className="ml-auto px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
          data-testid={`subject-rename-${subject.slug}`}
        >
          {t("subjects.rename")}
        </button>
        <button
          onClick={() => setShowVis(true)}
          className="px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
        >
          {t("subjects.visibility")}
        </button>
        <button
          onClick={() => setShowLevels(true)}
          className="px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
        >
          {t("subjects.levels.button")}
          {(subject.levels?.length ?? 0) > 0 && (
            <span className="ml-1 text-[10px] text-sky-600">{subject.levels!.length}</span>
          )}
        </button>
        <button
          onClick={() => archiveMut.mutate()}
          className="px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
        >
          {archived ? t("subjects.unarchive") : t("subjects.archive")}
        </button>
        {archived && (
          <button
            onClick={() => {
              if (window.confirm(t("subjects.deleteConfirm", { name: subject.name }))) delMut.mutate();
            }}
            className="px-2 py-0.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50"
          >
            {t("subjects.delete")}
          </button>
        )}
      </div>
      <FolderPanel
        subdir={`subjects/${subject.slug}`}
        heading={subject.name}
        hint={`subject_${subject.slug}`}
        collection={`subject_${subject.slug}`}
        subjectSlug={subject.slug}
      />
      {showVis && <VisibilityEditor subject={subject} onClose={() => setShowVis(false)} />}
      {showLevels && <LevelsEditor subject={subject} onClose={() => setShowLevels(false)} />}
    </div>
  );
}

const VIS_MODULES = [
  "workbench", "classes", "admissions", "itarget", "assignments",
  "exams", "reports", "teaching_plans", "documents", "my_promotions",
] as const;

/** 模块可见性预设编辑(§5.2 园长微调,存 module_visibility JSONB)。 */
function VisibilityEditor({ subject, onClose }: { subject: Subject; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [vis, setVis] = useState<Record<string, string>>(subject.module_visibility ?? {});
  const save = useMutation({
    mutationFn: async (clear: boolean) => {
      const r = await fetch(`/api/admin/subjects/${subject.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(clear ? { clear_visibility: true } : { module_visibility: vis }),
      });
      if (!r.ok) throw new Error(String(r.status));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subjects"] });
      onClose();
    },
  });
  // Portal 到 body:归档卡片的 opacity-50 会创建 stacking context,把
  // 内联渲染的 fixed 弹窗困在里面(弹窗半透明+被后续卡片盖住,表现为
  // "无法编辑/删除"——Jarod 2026-08-26)。
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[26rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800 mb-1">
          {subject.name} · {t("subjects.visibility")}
        </h3>
        <p className="mb-3 text-xs text-slate-400">{t("subjects.visibilityHint")}</p>
        <div className="mb-4 space-y-1.5 max-h-72 overflow-y-auto">
          {VIS_MODULES.map((m) => (
            <label key={m} className="flex items-center justify-between text-sm text-slate-700">
              {t(`subjects.modules.${m}`)}
              <select
                value={vis[m] ?? ""}
                onChange={(e) =>
                  setVis((v) => {
                    const next = { ...v };
                    if (e.target.value) next[m] = e.target.value;
                    else delete next[m];
                    return next;
                  })
                }
                className="rounded border border-slate-200 px-2 py-1 text-xs"
              >
                <option value="">{t("subjects.visDefault")}</option>
                <option value="visible">{t("subjects.visVisible")}</option>
                <option value="readonly">{t("subjects.visReadonly")}</option>
                <option value="hidden">{t("subjects.visHidden")}</option>
              </select>
            </label>
          ))}
        </div>
        <div className="flex justify-between">
          <button
            onClick={() => save.mutate(true)}
            className="px-3 py-1.5 rounded border border-slate-200 text-xs text-slate-500"
          >
            {t("subjects.visReset")}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
              {t("common.cancel")}
            </button>
            <button
              onClick={() => save.mutate(false)}
              disabled={save.isPending}
              className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}

function CreateSubjectButton({
  onCreate,
  disabled,
}: {
  onCreate: (input: { slug: string; name: string; description?: string }) => void;
  disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const [slugWarn, setSlugWarn] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // 标识只允许小写英文/数字/连字符(后端同款正则)。中文/大写/空格
  // 实时清洗掉并给出提示,别让老师提交后才吃一个裸 400。
  function onSlugChange(raw: string) {
    const cleaned = raw
      .toLowerCase()
      .replace(/[\s_]+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setSlugWarn(cleaned !== raw);
    setSlug(cleaned);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug.trim() || !name.trim()) return;
    onCreate({
      slug: slug.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
    });
    setSlug("");
    setName("");
    setDescription("");
    setOpen(false);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
      >
        {t("subjects.createButton")}
      </button>
      {open && (
        <form
          onSubmit={submit}
          className="absolute right-0 mt-2 w-80 z-10 bg-white border border-slate-200 rounded-lg shadow-lg p-3 space-y-2"
        >
          <div>
            <label className="block text-xs text-slate-600">
              {t("subjects.create.slugLabel")}
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => onSlugChange(e.target.value)}
              required
              placeholder="physics"
              className="w-full mt-1 rounded border border-slate-200 px-2 py-1 text-sm font-mono"
            />
            <p className={`mt-0.5 text-[11px] ${slugWarn ? "text-amber-600" : "text-slate-400"}`}>
              {t("subjects.create.slugHint")}
            </p>
          </div>
          <div>
            <label className="block text-xs text-slate-600">
              {t("subjects.create.nameLabel")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder={String(t("subjects.create.namePlaceholder"))}
              className="w-full mt-1 rounded border border-slate-200 px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600">
              {t("subjects.create.descriptionLabel")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full mt-1 rounded border border-slate-200 px-2 py-1 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs px-3 py-1 rounded text-slate-600 hover:bg-slate-100"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={disabled}
              className="text-xs px-3 py-1 rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
            >
              {t("subjects.create.submit")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


/** 学科分层设置:勾选该学科启用哪几级(可只启用两级,也可全不启用)。
 *  与 VisibilityEditor 同样 portal 到 body —— 归档卡的 opacity-50 会
 *  造 stacking context 困住内联 fixed 弹窗(2026-08-27 修过一次)。 */
function LevelsEditor({ subject, onClose }: { subject: Subject; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string[]>(subject.levels ?? []);
  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/admin/subjects/${subject.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ levels: picked }),
      });
      if (!r.ok) throw new Error(String(r.status));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subjects"] });
      onClose();
    },
  });
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[24rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800 mb-1">
          {subject.name} · {t("subjects.levels.title")}
        </h3>
        <p className="mb-3 text-xs text-slate-400">{t("subjects.levels.hint")}</p>
        <div className="mb-4 space-y-2">
          {LEVEL_KEYS.map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={picked.includes(k)}
                onChange={(e) =>
                  setPicked((prev) =>
                    e.target.checked ? [...prev, k] : prev.filter((x) => x !== k),
                  )
                }
              />
              <LevelBadge level={k} />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button className="px-3 py-1 text-xs rounded border border-slate-200" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            className="px-3 py-1 text-xs rounded bg-sky-600 text-white disabled:opacity-50"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  , document.body);
}
